/**
 * Contact ladders on the client (WP-X1, docs/CONTACTS.md; the rules are shared/contacts.ts). Order 538, after
 * the economy (510), the missions (530) and the side activities (535–537), so a job's own pay lands first.
 *
 *  - Offline it keeps the points in the save (`SaveData.contacts`) and listens for the work each contact cares
 *    about: Mags' story jobs, Halloran's export orders and workshop, Lenny's street jobs and matchbooks, Rita's
 *    races, Dot's minicab shifts, Sid's counter, Pike's respray and spray bay. A rung reached is a call in the
 *    book (the inbox de-duplicates by key) and a toast; pay bonuses are a separate line in the wallet
 *    (`contact:<track>`), so a job's own pay never changes.
 *  - Online the server is the truth (it derives standing from the ledger and applies every price itself);
 *    src/online-ui/firstHour.ts hands this service the server's standing so the book's ladders and the price
 *    tags agree with what the server will charge. Nothing here moves money online.
 *
 * `perk(key)` is how a price tag or a payout asks "what does my standing give me here?" in either mode.
 */
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import {
  SOURCES,
  TRACKS,
  award,
  bonusOn,
  dayOf,
  perk as perkOf,
  rungOf,
  rungPerks,
  sanitizeStanding,
  type ContactMode,
  type ContactTrack,
  type PerkKey,
  type SourceKey,
  type Standing,
} from '../../shared/contacts';
import { BORROWED_TIME, EXPORT_ORDER, GULLET_JOB, LAST_ORDERS, ONE_ARMED_BANDITS, STREET_JOB, THE_COLLECTION, WET_PAPER } from '../missions/ids';
import { RACE_ID } from '../activities/race';
import { CAB_ID } from '../activities/cab';

/** The story jobs (Mags' track). */
export const STORY_IDS: readonly string[] = [BORROWED_TIME, WET_PAPER, GULLET_JOB, ONE_ARMED_BANDITS, THE_COLLECTION, LAST_ORDERS];

export interface ContactsService {
  /** Offline (the save) or online (the server's word). */
  readonly mode: ContactMode;
  /** Points per track in the current mode. */
  standing(): Standing;
  rung(track: ContactTrack): number;
  /** What the standing gives for `key` right now (a fraction: 0.1 = 10 %). */
  perk(key: PerkKey): number;
  /** Points from repeat work used today on `track` (null when unknown). */
  todayUsed(track: ContactTrack): number | null;
  /** Online (src/online-ui): the server's standing (and today's repeat-work points), or null when leaving. */
  setOnline(st: Standing | null, today?: Standing): void;
  /** Bumped whenever the standing changes (the book redraws on it). */
  readonly version: number;
}

declare module '../core/services' {
  interface ServiceMap {
    contacts: ContactsService;
  }
}

declare module '../core/events' {
  interface GameEvents {
    /** A contact's rung went up (offline, or online when the server's standing arrives higher). */
    contactRung: { track: ContactTrack; rung: number; mode: ContactMode };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Contact ladders: mode, points and rung per track, today's capped points (tests / e2e). */
    contactsState(): Record<string, unknown>;
    /** Dev / test, offline only: as if `source` happened `times` times now. Returns the points gained. */
    contactsAward(source: string, times?: number): number;
  }
}

/** The perk lines for a rung, as one plain sentence ("Now: 10 % off Sid’s prices."). */
export function perkSentence(track: ContactTrack, rung: number, mode: ContactMode): string {
  const p = rungPerks(track, rung, mode).map((x) => x.text);
  const def = TRACKS[track].rungs[rung - 1];
  if (def?.title) p.push(`the title “${def.name}” in your book`);
  if (!p.length) return '';
  return `Now: ${p.join('; ')}.`;
}

/** The book message for a rung reached. */
export function rungMessage(track: ContactTrack, rung: number, mode: ContactMode): { key: string; from: string; kind: 'call'; subject: string; text: string } {
  const t = TRACKS[track];
  const r = t.rungs[rung - 1]!;
  return {
    key: `contact-rung:${mode}:${track}:${rung}`,
    from: t.name,
    kind: 'call',
    subject: `${t.short}: ${r.name}`,
    text: `“${r.line}” ${perkSentence(track, rung, mode)} Your book’s Contacts page has the whole ladder.`.trim(),
  };
}

export function createContactsSystem(): System {
  let ctx: GameContext;
  let online: Standing | null = null;
  let onlineToday: Standing = {};
  let version = 0;
  const offs: (() => void)[] = [];

  const save = () => ctx.services.tryGet('save');
  // Online is the network's word (the standing the server sent is kept for the session; back offline, the save).
  const isOnline = (): boolean => !!ctx.services.tryGet('net')?.online;
  /** Offline awards only: the single-player save, not suspended, the local wallet. */
  const offlineLive = (): boolean => !isOnline() && !!save() && !save()!.suspended && (ctx.services.tryGet('economy')?.authority ?? 'local') === 'local';

  const standing = (): Standing => (isOnline() ? online ?? {} : save()?.data.contacts.pts ?? {});

  function give(source: SourceKey, times = 1): number {
    const sv = save();
    if (!sv || !offlineLive()) return 0;
    const r = award(sv.data.contacts, source, dayOf(Date.now()), times);
    if (r.gained <= 0) return 0;
    sv.data.contacts = r.state;
    sv.markDirty();
    version++;
    for (let k = r.from + 1; k <= r.to; k++) reached(r.track, k, 'offline');
    return r.gained;
  }

  function reached(track: ContactTrack, rung: number, mode: ContactMode): void {
    ctx.events.emit('contactRung', { track, rung, mode });
    if (typeof window === 'undefined') return; // headless: the event is the record
    const m = rungMessage(track, rung, mode);
    ctx.services.tryGet('book')?.post(m);
    ctx.services.tryGet('ui')?.toast(`${TRACKS[track].short}: ${TRACKS[track].rungs[rung - 1]!.name} — see Contacts in your book`, 'good', 4);
  }

  /** A pay bonus from a contact on top of a job's pay (offline): its own line in the wallet. */
  function bonus(track: ContactTrack, key: PerkKey, base: number): void {
    if (!offlineLive() || base <= 0) return;
    const extra = bonusOn(base, perkOf(standing(), key, 'offline'));
    if (extra <= 0) return;
    ctx.services.tryGet('economy')?.earn(extra, `contact:${track}`);
    if (typeof window !== 'undefined') ctx.services.tryGet('ui')?.toast(`${TRACKS[track].short} adds £${extra.toLocaleString('en-GB')} on top`, 'good', 3);
  }

  const service: ContactsService = {
    get mode() {
      return isOnline() ? 'online' : 'offline';
    },
    standing,
    rung: (t) => rungOf(standing()[t] ?? 0),
    perk: (key) => perkOf(standing(), key, isOnline() ? 'online' : 'offline'),
    todayUsed(track) {
      if (isOnline()) return online ? onlineToday[track] ?? 0 : null;
      const c = save()?.data.contacts;
      if (!c) return null;
      return c.day === dayOf(Date.now()) ? c.today[track] ?? 0 : 0;
    },
    setOnline(st, today) {
      const prev = online;
      online = st ? sanitizeStanding(st) : null;
      onlineToday = today ? sanitizeStanding(today) : {};
      version++;
      if (!prev || !online) return;
      // A rung reached since the last word from the server (the first answer after joining is silent: a
      // veteran does not get eight calls for what they already have).
      for (const t of Object.keys(online) as ContactTrack[]) {
        const a = rungOf(prev[t] ?? 0);
        const b = rungOf(online[t] ?? 0);
        for (let k = a + 1; k <= b; k++) reached(t, k, 'online');
      }
    },
    get version() {
      return version;
    },
  };

  return {
    id: 'contacts',
    order: 539,
    init(c) {
      ctx = c;
      c.services.provide('contacts', service);
      offs.push(
        c.events.on('missionCompleted', (e) => {
          if (!offlineLive()) return;
          if (STORY_IDS.includes(e.id)) {
            give(e.replay ? 'story-replay' : 'story');
          } else if (e.id === EXPORT_ORDER) {
            bonus('des', 'export-pay', e.reward);
            give('export');
          } else if (e.id === STREET_JOB) {
            bonus('lenny', 'street-pay', e.reward);
            give('street');
          } else if (e.id === RACE_ID) {
            bonus('rita', 'race-pay', e.reward);
            give(e.reward > 0 ? 'race-win' : 'race');
          } else if (e.id === CAB_ID) {
            bonus('dot', 'cab-pay', e.reward);
            give('cab');
          }
        }),
        c.events.on('garageWork', () => void give('garage-work')),
        c.events.on('matchbookFound', () => void give('matchbook')),
        c.events.on('weaponBought', () => void give('fence-buy')),
        c.events.on('resprayed', (e) => {
          // The spray bay's paint is a respray too, but it counts once, as the bay's work (below).
          if (e.cost > 0 && !ctx.services.tryGet('sprayBay')?.isOpen) give('respray');
        }),
        c.events.on('sprayBayClosed', (e) => {
          if (e.paid > 0) give('spray-bay');
        }),
        c.events.on('newGameStarted', () => {
          version++;
        }),
      );
      c.hooks.register('contactsState', () => {
        const st = standing();
        const out: Record<string, unknown> = { mode: service.mode, version };
        for (const t of Object.keys(TRACKS) as ContactTrack[]) out[t] = { points: st[t] ?? 0, rung: rungOf(st[t] ?? 0) };
        out.today = save()?.data.contacts.today ?? {};
        return out;
      });
      c.hooks.register('contactsAward', (source, times = 1) => (Object.prototype.hasOwnProperty.call(SOURCES, source) ? give(source as SourceKey, Math.max(1, Math.min(50, Math.floor(times)))) : 0));
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
